import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  adoptManageTracks,
  createManageTrack,
  deleteManageTrack,
  listManageTracks,
  reorderManageTracks,
  updateManageTrack,
  type TrackInput,
  type LocationRow,
} from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ManageClubNav, moveId, useManagedClub } from "../components/ManageClubNav";
import { PencilFillIcon, Trash3FillIcon } from "../components/icons";
import Toast from "../components/Toast";
import { MapPreview } from "../components/MapPreview";

const EMPTY: TrackInput = { name: "", address: "", description: "", phone: "", website: "", published: true };

/** /manage/:clubId/tracks -- the club's tracks, listed on the Tracks page of
 *  the club's own site: name, address (with a map preview once saved),
 *  description, phone, website. Also offered as Location suggestions on the
 *  Manage event form. */
export default function ManageTracksPage() {
  const { clubId = "" } = useParams();
  const { user } = useAuth();
  const { club, error: clubError } = useManagedClub(clubId);
  const [tracks, setTracks] = useState<LocationRow[] | null>(null);
  const [form, setForm] = useState<TrackInput>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function refresh() {
    listManageTracks(clubId)
      .then((res) => setTracks(res.tracks))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load tracks."));
  }
  useEffect(refresh, [clubId]);

  async function run(action: () => Promise<string | void>) {
    setError(null);
    setBusy(true);
    try {
      const msg = await action();
      if (msg) setInfo(msg);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  function startEdit(s: LocationRow) {
    setEditingId(s.locationId);
    setForm({
      name: s.name,
      address: s.address ?? "",
      phone: s.phone ?? "",
      description: s.description ?? "",
      website: s.website ?? "",
      published: s.published !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    run(async () => {
      if (editingId) {
        await updateManageTrack(clubId, editingId, form);
      } else {
        // Stay on the new track after adding it, so its map preview shows.
        const { track } = await createManageTrack(clubId, form);
        setEditingId(track.locationId);
      }
      refresh();
      return `Saved ${form.name}.`;
    });
  }

  function move(id: string, delta: -1 | 1) {
    if (!tracks) return;
    const ids = moveId(tracks.map((s) => s.locationId), id, delta);
    run(async () => setTracks((await reorderManageTracks(clubId, ids)).tracks));
  }

  if (clubError) return <p className="err">{clubError}</p>;
  if (!club) return <p>Loading…</p>;

  // The map previews the saved address, not what's half-typed in the box.
  const saved = tracks?.find((s) => s.locationId === editingId);

  return (
    <div>
      <ManageClubNav club={club} />
      <h1>{club.name} tracks</h1>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <form onSubmit={handleSubmit} className="card">
        <h3>{editingId ? "Edit track" : "Add a track"}</h3>
        <label htmlFor="track-name">Name</label>
        <input id="track-name" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor="track-address">Address</label>
          <input
            id="track-address"
            autoComplete="street-address"
            placeholder="123 Main St, Spokane, WA 99201"
            value={form.address ?? ""}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </div>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <div>
            <label htmlFor="track-phone">Phone</label>
            <input id="track-phone" type="tel" value={form.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label htmlFor="track-website">Website</label>
            <input id="track-website" placeholder="https://" value={form.website ?? ""} onChange={(e) => setForm({ ...form, website: e.target.value })} />
          </div>
        </div>
        <div style={{ marginTop: "0.75rem" }}>
          <label htmlFor="track-description">Description</label>
          <textarea
            id="track-description"
            rows={4}
            placeholder="Surface, layout, classes run, practice days…"
            value={form.description ?? ""}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.75rem" }}>
          <input type="checkbox" style={{ width: "auto" }} checked={form.published !== false} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
          Show on the Tracks page
        </label>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
          <button type="submit" disabled={busy}>{editingId ? "Save changes" : "Add track"}</button>
          {editingId && <button type="button" className="secondary" onClick={resetForm}>Done</button>}
        </div>
        {saved?.address && <MapPreview address={saved.address} name={saved.name} />}
      </form>

      <h2>Page order</h2>
      {tracks && tracks.length === 0 && <p className="muted">No tracks yet.</p>}
      {tracks && tracks.length > 0 && (
        <div className="table-scroll"><table>
          <thead>
            <tr><th>Order</th><th>Name</th><th>Phone</th><th>Shown</th><th /></tr>
          </thead>
          <tbody>
            {tracks.map((s, i) => (
              <tr key={s.locationId}>
                <td>
                  <span className="order-buttons">
                    <button type="button" className="secondary" disabled={busy || i === 0} onClick={() => move(s.locationId, -1)} aria-label={`Move ${s.name} up`}>↑</button>
                    <button type="button" className="secondary" disabled={busy || i === tracks.length - 1} onClick={() => move(s.locationId, 1)} aria-label={`Move ${s.name} down`}>↓</button>
                  </span>
                </td>
                <td>
                  {s.name}
                  {s.address && <div className="muted" style={{ fontSize: 13 }}>{s.address}</div>}
                </td>
                <td>{s.phone ?? ""}</td>
                <td>{s.published === false ? "Hidden" : "Yes"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button type="button" className="link" title="Edit" aria-label={`Edit ${s.name}`} onClick={() => startEdit(s)}>
                    <PencilFillIcon />
                  </button>
                  <button
                    type="button"
                    className="link"
                    title="Delete"
                    aria-label={`Delete ${s.name}`}
                    onClick={() =>
                      confirm(`Delete ${s.name}?`) &&
                      run(async () => {
                        await deleteManageTrack(clubId, s.locationId);
                        if (editingId === s.locationId) resetForm();
                        refresh();
                        return `Deleted ${s.name}.`;
                      })
                    }
                  >
                    <Trash3FillIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {user?.isAdmin && (
        <p className="muted" style={{ marginTop: "1.5rem" }}>
          Site admin:{" "}
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() =>
              confirm(`Move every site-wide track (from Admin) into ${club.name}? They will then only show on the club site.`) &&
              run(async () => {
                const res = await adoptManageTracks(clubId);
                setTracks(res.tracks);
                return `Moved ${res.adopted} track${res.adopted === 1 ? "" : "s"} into ${club.name}.`;
              })
            }
          >
            Adopt site-wide tracks
          </button>
        </p>
      )}
    </div>
  );
}
